import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Flag, Milestone, Search, Wallet, AlertTriangle, FolderKanban } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import BudgetOverview from '../BudgetOverview';
import ProjectStatus from '../ProjectStatus';
import ProjectTimeline from './ProjectTimeline';
import ProjectRisks from './ProjectRisks';
import ProjectMilestones from './ProjectMilestones';
import { t } from '../../i18n';

const ProjectsHome = () => {
  const { parks, preferences, updatePreferences } = useAppContext();
  const preferredParkId = preferences?.projectContext?.parkId || 'all';

  const [query, setQuery] = useState('');
  const [selectedParkId, setSelectedParkId] = useState(preferredParkId === 'all' ? '' : preferredParkId);
  const [activeTab, setActiveTab] = useState('budget');

  useEffect(() => {
    if (!Array.isArray(parks) || parks.length === 0) return;
    if (preferredParkId !== 'all' && parks.some((park) => park.id === preferredParkId)) {
      setSelectedParkId(preferredParkId);
    }
  }, [parks, preferredParkId]);

  useEffect(() => {
    if (!selectedParkId) return;
    updatePreferences({ projectContext: { parkId: selectedParkId } });
  }, [selectedParkId, updatePreferences]);

  const filteredParks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return parks;
    return parks.filter((park) => {
      const name = String(park?.name || '').toLowerCase();
      const code = String(park?.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [parks, query]);

  const selectedPark = useMemo(
    () => parks.find((park) => park.id === selectedParkId) || null,
    [parks, selectedParkId]
  );

  const tabs = [
    { id: 'budget', label: t('Presupuesto'), icon: Wallet },
    { id: 'timeline', label: t('Cronograma'), icon: Calendar },
    { id: 'risks', label: t('Avance y riesgos'), icon: AlertTriangle },
    { id: 'milestones', label: t('Registro de hitos'), icon: Flag },
    { id: 'status', label: t('Status general'), icon: Milestone }
  ];

  const renderTab = () => {
    if (!selectedPark) return null;
    switch (activeTab) {
      case 'budget':
        return <BudgetOverview embedded parkId={selectedPark.id} />;
      case 'timeline':
        return <ProjectTimeline embedded parkId={selectedPark.id} />;
      case 'risks':
        return <ProjectRisks embedded parkId={selectedPark.id} />;
      case 'milestones':
        return <ProjectMilestones embedded parkId={selectedPark.id} />;
      case 'status':
        return <ProjectStatus embedded />;
      default:
        return null;
    }
  };

  if (!parks || parks.length === 0) {
    return (
      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
        <h1 className="text-3xl font-bold text-slate-900">{t('Proyectos')}</h1>
        <p className="text-sm text-slate-500 mt-2">{t(
          'Aún no hay proyectos creados. Crea un proyecto para habilitar Presupuesto, Cronograma, Avance y Status.'
        )}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Proyectos')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Busca y selecciona un proyecto. Luego elige el submódulo para ver la información.'
          )}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="text-xs font-semibold text-slate-500">{t('Buscar proyecto')}</label>
            <div className="mt-2 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('Nombre o código del proyecto...')}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-2xl bg-white text-base"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500">{t('Seleccionar proyecto')}</label>
            <select
              value={selectedParkId}
              onChange={(event) => setSelectedParkId(event.target.value)}
              className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-2xl bg-white text-base font-semibold"
            >
              <option value="" disabled>{t('Selecciona...')}</option>
              {filteredParks.map((park) => (
                <option key={park.id} value={park.id}>
                  {t(park.name)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedPark && (
          <div className="mt-5 flex items-center gap-3 text-sm">
            <div className="p-2 rounded-xl bg-slate-100 border border-slate-200">
              <FolderKanban className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <p className="font-bold text-slate-900">{t(selectedPark.name)}</p>
              <p className="text-xs text-slate-500">{t(selectedPark.code) || t(selectedPark.id)}</p>
            </div>
          </div>
        )}
      </div>

      {selectedPark && (
        <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 rounded-2xl border text-sm font-semibold inline-flex items-center gap-2 transition ${
                    active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(tab.label)}
                </button>
              );
            })}
          </div>

          <div className="mt-6">{renderTab()}</div>
        </div>
      )}
    </div>
  );
};

export default ProjectsHome;

